import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Translations live in src/locales/<lang>/<section>.json.
 * Every file in a language folder is deep-merged into that language's
 * single "translation" namespace, so a page can own its own file
 * (e.g. locales/en/home.json) without touching the others.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ctx = (require as any).context('./locales', true, /\.json$/);

const deepMerge = (target: Record<string, any>, source: Record<string, any>) => {
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      target[key] = deepMerge(target[key] ? { ...target[key] } : {}, source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
};

const resources: Record<string, { translation: Record<string, any> }> = {};
ctx.keys().forEach((key: string) => {
  const match = key.match(/^\.\/([a-z]{2})\/[^/]+\.json$/);
  if (!match) return;
  const lang = match[1];
  resources[lang] = resources[lang] || { translation: {} };
  deepMerge(resources[lang].translation, ctx(key));
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar'],
    debug: false,
    interpolation: { escapeValue: false },
    detection: { order: ['localStorage', 'navigator'], caches: ['localStorage'] },
    react: { useSuspense: true },
  });

export const isRTL = () => i18n.language?.startsWith('ar');

export const setDocumentDirection = (language: string) => {
  const rtl = language.startsWith('ar');
  document.documentElement.dir = rtl ? 'rtl' : 'ltr';
  document.documentElement.lang = rtl ? 'ar' : 'en';
};

i18n.on('languageChanged', setDocumentDirection);
setDocumentDirection(i18n.language || 'en');

export default i18n;
